import {
    ChevronDown,
    ChevronRight,
    Files,
    Loader2,
} from "lucide-react";
import { useT } from "@magic-md/i18n/react";
import { useCallback, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { fileNameFromPath } from "../lib/path-utils";
import { cn } from "../lib/utils";
import { useEditorStore } from "../store/editor-store";
import { usePluginExecutionStore } from "../store/plugin-execution-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";
import { FileIcon } from "../theme/runtime/theme-icons";
import { ContextMenu, type ContextMenuItem } from "./ContextMenu";
import { buildOpenFileMenuItems } from "./context-menu-builders";
import { DocumentFileTreeNode } from "./DocumentFileTreeNode";
import { SidebarCloseButton } from "./SidebarCloseButton";

interface FileTreeItem {
    key: string;
    tabId: string | null;
    title: string;
    filePath: string | null;
    importNamespace?: string;
    isDirty: boolean;
    isOpen: boolean;
    isCycle?: boolean;
    children: FileTreeItem[];
}

function resolveImportPath(parentFilePath: string, importSource: string): string {
    const lastSlash = parentFilePath.lastIndexOf("/");
    const dir = lastSlash >= 0 ? parentFilePath.substring(0, lastSlash) : "";
    const raw = dir ? `${dir}/${importSource}` : importSource;
    const parts = raw.split("/");
    const resolved: string[] = [];
    for (const part of parts) {
        if (part === "." || part === "") continue;
        if (part === ".." && resolved.length > 0 && resolved[resolved.length - 1] !== "..") {
            resolved.pop();
        } else {
            resolved.push(part);
        }
    }
    return "/" + resolved.join("/");
}

const MAX_TREE_DEPTH = 5;

function buildImportChildren(
    tabId: string,
    filePath: string,
    parsedDocumentMap: Map<string, unknown>,
    tabsByFilePath: Map<string, { id: string; title: string; isDirty: boolean }>,
    visited: Set<string>,
    depth: number,
): FileTreeItem[] {
    if (depth >= MAX_TREE_DEPTH) return [];
    const parsed = parsedDocumentMap.get(tabId) as
        | { imports?: { source: string; namespace: string }[] }
        | undefined;
    if (!parsed?.imports?.length) return [];

    const children: FileTreeItem[] = [];
    for (const imp of parsed.imports) {
        const resolvedPath = resolveImportPath(filePath, imp.source);

        if (visited.has(resolvedPath)) {
            children.push({
                key: `cycle-${resolvedPath}`,
                tabId: null,
                title: `${fileNameFromPath(imp.source)} ↻`,
                filePath: resolvedPath,
                importNamespace: imp.namespace,
                isDirty: false,
                isOpen: false,
                isCycle: true,
                children: [],
            });
            continue;
        }
        visited.add(resolvedPath);

        const openTab = tabsByFilePath.get(resolvedPath);
        const childItem: FileTreeItem = {
            key: openTab ? `dep-${openTab.id}` : `dep-${resolvedPath}`,
            tabId: openTab?.id ?? null,
            title: openTab?.title ?? fileNameFromPath(imp.source),
            filePath: resolvedPath,
            importNamespace: imp.namespace,
            isDirty: openTab?.isDirty ?? false,
            isOpen: !!openTab,
            children: [],
        };

        if (openTab?.id) {
            childItem.children = buildImportChildren(
                openTab.id,
                resolvedPath,
                parsedDocumentMap,
                tabsByFilePath,
                visited,
                depth + 1,
            );
        }

        children.push(childItem);
    }
    return children;
}

function FileTreeNodeRow({
    item,
    depth,
    indentBase = 8,
    onActivate,
    onOpen,
    onOpenToSide,
    onCloseTab,
}: {
    item: FileTreeItem;
    depth: number;
    indentBase?: number;
    onActivate: (tabId: string) => void;
    onOpen: (filePath: string) => void;
    onOpenToSide: (item: FileTreeItem) => void;
    onCloseTab: (tabId: string) => void;
}) {
    const t = useT("editor");
    const [expanded, setExpanded] = useState(false);
    const [ctxMenu, setCtxMenu] = useState<{
        open: boolean;
        x: number;
        y: number;
    } | null>(null);
    const hasChildren = item.children.length > 0;
    const isActive = useEditorStore(
        (s) => item.tabId != null && s.activeTabId === item.tabId,
    );
    const isBackgroundRunning = usePluginExecutionStore(
        (s) =>
            !isActive &&
            item.tabId != null &&
            s.backgroundRunningTabs.has(item.tabId),
    );

    const handleClick = useCallback(() => {
        if (item.isCycle) return;
        if (item.tabId) {
            onActivate(item.tabId);
        } else if (item.filePath) {
            onOpen(item.filePath);
        }
    }, [item.tabId, item.filePath, item.isCycle, onActivate, onOpen]);

    const handleChevronClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setExpanded((v) => !v);
    }, []);

    const handleContextMenu = useCallback((e: React.MouseEvent) => {
        if (!item.filePath) return;
        e.preventDefault();
        e.stopPropagation();
        setCtxMenu({ open: true, x: e.clientX, y: e.clientY });
    }, [item.filePath]);

    const contextItems = useMemo<ContextMenuItem[]>(() => {
        if (!item.filePath) return [];
        return buildOpenFileMenuItems({
            path: item.filePath,
            t,
            title: item.title,
            tabId: item.tabId,
            tabs: useEditorStore.getState().tabs,
            isCycle: item.isCycle,
            onOpen: handleClick,
            onOpenToSide: () => onOpenToSide(item),
            closeTab: onCloseTab,
        });
    }, [item, handleClick, onCloseTab, onOpenToSide]);

    if (item.filePath && /\.md$/i.test(item.filePath)) {
        return (
            <DocumentFileTreeNode
                filePath={item.filePath}
                name={item.title}
                depth={depth}
                indentBase={indentBase}
                isActive={isActive}
                isDirty={item.isDirty}
                isCycle={item.isCycle}
                isSelected={false}
                showDoctorNode={false}
                contextItems={contextItems}
                onActivate={item.tabId ? () => onActivate(item.tabId!) : undefined}
                onOpenFile={onOpen}
                suffix={
                    item.importNamespace ? (
                        <span className="text-[9px] font-mono text-muted-foreground/60 bg-muted/40 rounded px-1 shrink-0">
                            {item.importNamespace}
                        </span>
                    ) : undefined
                }
                trailing={
                    isBackgroundRunning ? (
                        <span title={t("openFiles.backgroundRunning")} className="shrink-0 ml-auto inline-flex">
                            <Loader2
                                size={11}
                                className="animate-spin theme-accent-text"
                                style={getThemeAccentStyle("warning")}
                            />
                        </span>
                    ) : undefined
                }
                extraChildren={
                    hasChildren
                        ? item.children.map((child) => (
                            <FileTreeNodeRow
                                key={child.key}
                                item={child}
                                depth={depth + 1}
                                indentBase={indentBase}
                                onActivate={onActivate}
                                onOpen={onOpen}
                                onOpenToSide={onOpenToSide}
                                onCloseTab={onCloseTab}
                            />
                        ))
                        : undefined
                }
            />
        );
    }

    return (
        <>
            <button
                type="button"
                onClick={handleClick}
                onContextMenu={handleContextMenu}
                className={cn(
                    "relative flex items-center gap-1.5 w-full py-1.5 rounded text-xs transition-colors",
                    item.isCycle
                        ? "text-muted-foreground/40 cursor-default italic"
                        : isActive
                            ? "bg-primary/10 text-primary"
                            : item.isOpen
                                ? "text-foreground/80 hover:bg-muted/60"
                                : "text-foreground/40 hover:bg-muted/40",
                )}
                style={{ paddingLeft: `${indentBase + depth * 16}px`, paddingRight: 8 }}
                title={item.isCycle ? t("openFiles.circularImport", { path: item.filePath }) : (item.filePath ?? item.title)}
            >
                {hasChildren ? (
                    <span
                        className="absolute top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center shrink-0 cursor-pointer"
                        onClick={handleChevronClick}
                        role="button"
                        tabIndex={-1}
                        style={{ left: `${Math.max(0, indentBase - 16) + depth * 16}px` }}
                    >
                        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    </span>
                ) : null}

                <FileIcon name={item.title} size={13} className="shrink-0" />
                <span className="truncate">{item.title}</span>

                {item.importNamespace && (
                    <span className="text-[9px] font-mono text-muted-foreground/60 bg-muted/40 rounded px-1 shrink-0">
                        {item.importNamespace}
                    </span>
                )}

                {isBackgroundRunning ? (
                    <span title={t("openFiles.backgroundRunning")} className="shrink-0 ml-auto inline-flex">
                        <Loader2
                            size={11}
                            className="animate-spin theme-accent-text"
                            style={getThemeAccentStyle("warning")}
                        />
                    </span>
                ) : item.isDirty ? (
                    <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 ml-auto" />
                ) : null}
            </button>

            {expanded &&
                item.children.map((child) => (
                    <FileTreeNodeRow
                        key={child.key}
                        item={child}
                        depth={depth + 1}
                        indentBase={indentBase}
                        onActivate={onActivate}
                        onOpen={onOpen}
                        onOpenToSide={onOpenToSide}
                        onCloseTab={onCloseTab}
                    />
                ))}
            {ctxMenu?.open && item.filePath && (
                <ContextMenu
                    open={ctxMenu.open}
                    x={ctxMenu.x}
                    y={ctxMenu.y}
                    items={contextItems}
                    onClose={() => setCtxMenu(null)}
                />
            )}
        </>
    );
}

export function OpenFilesSection({ embedded = false }: { embedded?: boolean }) {
    const t = useT("editor");
    const [expanded, setExpanded] = useState(false);
    const tabSignatures = useEditorStore(
        useShallow((s) =>
            s.tabs.map(
                (tab) =>
                    `${tab.id}\u0000${tab.title}\u0000${tab.isDirty ? "1" : "0"}\u0000${tab.filePath ?? ""}`,
            ),
        ),
    );
    const importSignatures = useEditorStore(
        useShallow((s) =>
            s.tabs.map((tab) => {
                const parsed = s.parsedDocumentMap.get(tab.id) as
                    | { imports?: { source: string; namespace: string }[] }
                    | undefined;
                if (!parsed?.imports?.length) return "";
                return parsed.imports
                    .map((imp) => `${imp.namespace}:${imp.source}`)
                    .join(",");
            }),
        ),
    );
    const setActiveTab = useEditorStore((s) => s.setActiveTab);
    const openFileFromPath = useEditorStore((s) => s.openFileFromPath);
    const closeTab = useEditorStore((s) => s.closeTab);
    const splitPane = useEditorStore((s) => s.splitPane);

    const { rootItems } = useMemo(() => {
        const tabs = tabSignatures.map((sig) => {
            const [id, title, dirtyFlag, filePath] = sig.split("\u0000");
            return { id, title, isDirty: dirtyFlag === "1", filePath: filePath || null };
        });

        const tabsByFilePath = new Map<string, { id: string; title: string; isDirty: boolean }>();
        for (const tab of tabs) {
            if (tab.filePath) tabsByFilePath.set(tab.filePath, tab);
        }

        const parsedDocumentMap = useEditorStore.getState().parsedDocumentMap;
        const childFilePaths = new Set<string>();
        const tabItems: FileTreeItem[] = [];

        for (const tab of tabs) {
            const visited = new Set<string>();
            if (tab.filePath) visited.add(tab.filePath);

            const children =
                tab.filePath
                    ? buildImportChildren(
                        tab.id,
                        tab.filePath,
                        parsedDocumentMap,
                        tabsByFilePath,
                        visited,
                        0,
                    )
                    : [];

            const collectPaths = (items: FileTreeItem[]) => {
                for (const item of items) {
                    if (item.filePath && !item.isCycle) childFilePaths.add(item.filePath);
                    collectPaths(item.children);
                }
            };
            collectPaths(children);

            tabItems.push({
                key: tab.id,
                tabId: tab.id,
                title: tab.title,
                filePath: tab.filePath,
                isDirty: tab.isDirty,
                isOpen: true,
                children,
            });
        }

        return {
            rootItems: tabItems.filter(
                (item) => !item.filePath || !childFilePaths.has(item.filePath),
            ),
        };
    }, [tabSignatures, importSignatures]);

    const openToSide = useCallback(async (item: FileTreeItem) => {
        if (item.tabId) {
            splitPane("vertical", item.tabId);
            return;
        }
        if (!item.filePath) return;
        await openFileFromPath(item.filePath);
        const tab = useEditorStore.getState().tabs.find((t) => t.filePath === item.filePath);
        if (tab) splitPane("vertical", tab.id);
    }, [openFileFromPath, splitPane]);

    const rowActions = {
        onActivate: setActiveTab,
        onOpen: openFileFromPath,
        onOpenToSide: (item: FileTreeItem) => void openToSide(item),
        onCloseTab: closeTab,
    };

    if (rootItems.length === 0) return null;

    if (!embedded) {
        return (
            <div className="flex flex-col h-full">
                <div className="flex items-center justify-between h-8 px-3 border-b border-border shrink-0">
                    <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                        {t("sidebar.openFiles")}
                    </span>
                    <SidebarCloseButton />
                </div>
                <div className="flex-1 min-h-0 overflow-auto p-1.5">
                    {rootItems.map((item) => (
                        <FileTreeNodeRow
                            key={item.key}
                            item={item}
                            depth={0}
                            {...rowActions}
                        />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="border-b border-border/50 pb-1 mb-1">
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="flex h-6 items-center w-full text-left text-xs px-2 rounded hover:bg-muted/60"
            >
                <span className="w-4 h-4 flex items-center justify-center shrink-0">
                    {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </span>
                <Files
                    size={12}
                    className="text-muted-foreground mr-1.5 shrink-0"
                />
                <span className="text-[11px] leading-none text-muted-foreground uppercase tracking-wider">
                    {t("openFiles.titleWithCount", { count: rootItems.length })}
                </span>
            </button>
            {expanded && (
                <div>
                    {rootItems.map((item) => (
                        <FileTreeNodeRow
                            key={item.key}
                            item={item}
                            depth={0}
                            indentBase={20}
                            {...rowActions}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
